/**
 * FeedbackButton - Floating button that opens a feedback modal
 *
 * Allows users to submit bug reports and feature requests as support tickets.
 * Optionally captures a page screenshot before opening the modal.
 */

import * as React from "react";
import { createPortal } from "react-dom";
import { toPng } from "html-to-image";
import { Bug, Lightbulb, Send, CheckCircle, Camera } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/ui/atoms/Button";
import { Modal, DialogFooter } from "@/ui/atoms/Modal";
import { FormField } from "@/ui/molecules/FormField";
import { cn } from "@/utils/utils";
import { getNow } from "@/utils/dateService";
import type {
    TCreateSupportTicketRequest,
    TApiSupportTicketResponse,
} from "@/apis/proda_finance/types";

// Must match the server's SupportTicketCreate.screenshot_base64 max_length.
const MAX_SCREENSHOT_BASE64_LENGTH = 2_000_000;

type TFeedbackType = "bug" | "feature";

export type TFeedbackButtonProps = {
    onSubmit: (data: TCreateSupportTicketRequest) => Promise<TApiSupportTicketResponse>;
    /** Whether the user may submit bug reports (report_issue_submit; admin only). */
    canSubmitBug?: boolean;
    /** Whether the user may open the Support ticket pages (report_view; PO only). */
    canViewTickets?: boolean;
};

type TSubmitState = "idle" | "submitting" | "success" | "error";

export const FeedbackButton = React.memo(function FeedbackButton({
    onSubmit,
    canSubmitBug = false,
    canViewTickets = false,
}: TFeedbackButtonProps) {
    // Idea submission is universal; bug reports are admin-only. When the user
    // cannot file bugs the modal is idea-only and the type toggle is hidden.
    const defaultType: TFeedbackType = canSubmitBug ? "bug" : "feature";
    const [isOpen, setIsOpen] = React.useState(false);
    const [feedbackType, setFeedbackType] = React.useState<TFeedbackType>(defaultType);
    const [title, setTitle] = React.useState("");
    const [description, setDescription] = React.useState("");
    const [submitState, setSubmitState] = React.useState<TSubmitState>("idle");
    const [errorMessage, setErrorMessage] = React.useState("");
    const [ticketId, setTicketId] = React.useState<string | null>(null);

    // Screenshot state
    const [screenshotBase64, setScreenshotBase64] = React.useState<string | null>(null);
    const [includeScreenshot, setIncludeScreenshot] = React.useState(false);

    const navigate = useNavigate();

    const resetForm = React.useCallback(() => {
        setFeedbackType(defaultType);
        setTitle("");
        setDescription("");
        setSubmitState("idle");
        setErrorMessage("");
        setTicketId(null);
        setScreenshotBase64(null);
        setIncludeScreenshot(false);
    }, [defaultType]);

    const handleOpenChange = React.useCallback(
        (open: boolean) => {
            if (!open && submitState === "submitting") return;
            setIsOpen(open);
            if (!open) {
                // Reset after close animation
                setTimeout(resetForm, 200);
            }
        },
        [submitState, resetForm],
    );

    const handleButtonClick = React.useCallback(async () => {
        // Capture screenshot before opening the modal so it doesn't cover the page.
        // Server caps `screenshot_base64` at MAX_SCREENSHOT_BASE64_LENGTH chars —
        // start crisp (up to 2× pixelRatio) and fall back to 1× if the page is
        // large enough that the hi-res capture would be rejected. Skip the
        // screenshot entirely rather than fail submission.
        const preferredRatio = Math.min(window.devicePixelRatio || 1, 2);
        const ratios = preferredRatio === 1 ? [1] : [preferredRatio, 1];

        let base64: string | null = null;
        for (const pixelRatio of ratios) {
            try {
                const dataUrl = await toPng(document.body, {
                    cacheBust: true,
                    pixelRatio,
                });
                const candidate = dataUrl.split(",")[1] ?? null;
                if (candidate && candidate.length <= MAX_SCREENSHOT_BASE64_LENGTH) {
                    base64 = candidate;
                    break;
                }
            } catch {
                // Try the next (lower) ratio
            }
        }
        setScreenshotBase64(base64);

        setIsOpen(true);
    }, []);

    const handleSubmit = React.useCallback(async () => {
        if (description.trim().length < 1) return;

        setSubmitState("submitting");
        setErrorMessage("");

        try {
            const label = feedbackType === "bug" ? "Bug report" : "Feature request";
            const now = getNow().toLocaleString();
            const ticketTitle = title.trim() || `${label} — ${now}`;

            const data: TCreateSupportTicketRequest = {
                title: ticketTitle,
                description: description.trim(),
                ticket_type: feedbackType,
                page_url: window.location.href,
                user_agent: window.navigator.userAgent,
                screenshot_base64: includeScreenshot ? screenshotBase64 : null,
            };

            const response = await onSubmit(data);
            setTicketId(response.id);
            setSubmitState("success");
        } catch (error) {
            const msg =
                error instanceof Error
                    ? error.message
                    : "Failed to submit feedback. Please try again.";
            setSubmitState("error");
            setErrorMessage(msg);
        }
    }, [description, feedbackType, title, onSubmit, includeScreenshot, screenshotBase64]);

    const isValid = description.trim().length >= 1;

    return createPortal(
        <>
            <button
                onPointerDown={(e) => e.stopPropagation()}
                onClick={handleButtonClick}
                className="pointer-events-auto fixed bottom-6 right-6 z-[9999] flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-105 hover:bg-proda-blue-50 active:scale-95"
                aria-label="Report a bug or suggest a feature"
            >
                <Bug className="h-5 w-5" />
            </button>

            <Modal
                open={isOpen}
                onOpenChange={handleOpenChange}
                title={submitState === "success" ? "Feedback Sent" : "Send Feedback"}
                className="z-[9998] max-w-md"
                overlayClassName="z-[9998]"
            >
                {submitState === "success" ? (
                    <div className="flex flex-col items-center gap-3 py-4">
                        <CheckCircle className="h-12 w-12 text-success" />
                        <p className="text-center text-sm text-muted-foreground">
                            Thank you for your feedback! A ticket has been created.
                        </p>
                        {ticketId && canViewTickets && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setIsOpen(false);
                                    setTimeout(resetForm, 200);
                                    navigate(`/support/${ticketId}`);
                                }}
                            >
                                View Ticket
                            </Button>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="space-y-4">
                            {/* Feedback type toggle — only when the user may file bugs */}
                            {canSubmitBug && (
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setFeedbackType("bug")}
                                        className={cn(
                                            "flex flex-1 items-center justify-center gap-2 rounded-lg border-2 px-3 py-2.5 text-sm font-medium transition-colors",
                                            feedbackType === "bug"
                                                ? "border-primary bg-primary/5 text-primary"
                                                : "border-border text-muted-foreground hover:border-primary/30 hover:text-foreground",
                                        )}
                                    >
                                        <Bug className="h-4 w-4" />
                                        Bug Report
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setFeedbackType("feature")}
                                        className={cn(
                                            "flex flex-1 items-center justify-center gap-2 rounded-lg border-2 px-3 py-2.5 text-sm font-medium transition-colors",
                                            feedbackType === "feature"
                                                ? "border-primary bg-primary/5 text-primary"
                                                : "border-border text-muted-foreground hover:border-primary/30 hover:text-foreground",
                                        )}
                                    >
                                        <Lightbulb className="h-4 w-4" />
                                        Feature Idea
                                    </button>
                                </div>
                            )}

                            {/* Title */}
                            <FormField label="Title" htmlFor="feedback-title">
                                <input
                                    id="feedback-title"
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="Brief summary (optional)"
                                    maxLength={255}
                                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                    disabled={submitState === "submitting"}
                                />
                            </FormField>

                            {/* Description */}
                            <FormField
                                label="Description"
                                htmlFor="feedback-description"
                                required
                                error={submitState === "error" ? errorMessage : undefined}
                            >
                                <textarea
                                    id="feedback-description"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    rows={4}
                                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                    disabled={submitState === "submitting"}
                                />
                            </FormField>

                            {/* Screenshot checkbox + preview */}
                            {screenshotBase64 && (
                                <div className="space-y-2">
                                    <label className="flex items-center gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={includeScreenshot}
                                            onChange={(e) => setIncludeScreenshot(e.target.checked)}
                                            className="h-4 w-4 rounded border-input"
                                            disabled={submitState === "submitting"}
                                        />
                                        <Camera className="h-4 w-4 text-muted-foreground" />
                                        <span className="text-muted-foreground">
                                            Include screenshot
                                        </span>
                                    </label>
                                    {includeScreenshot && (
                                        <img
                                            src={`data:image/png;base64,${screenshotBase64}`}
                                            alt="Page screenshot preview"
                                            className="max-h-32 w-full rounded-md border border-border object-contain object-left-top"
                                        />
                                    )}
                                </div>
                            )}
                        </div>

                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => handleOpenChange(false)}
                                disabled={submitState === "submitting"}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                disabled={!isValid}
                                loading={submitState === "submitting"}
                                leftIcon={<Send className="h-4 w-4" />}
                            >
                                Submit
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </Modal>
        </>,
        document.body,
    );
});
